<template>
  <div class="search-results-carousel">
    <!-- Neural Animation Row - Always visible when searching -->
    <div v-if="!finalAnswerReceived" class="neural-row">
      <div class="status-wrapper">
        <div class="status-icon">
          <div class="neural-animation">
            <div class="neural-pulse"></div>
            <div class="neural-ring"></div>
          </div>
        </div>
        <div class="status-content">
          <div class="status-progress">
            <div class="waveform">
              <div v-for="n in 24" :key="n"
                   class="waveform-bar"
                   :class="{ 'active': isBarActive(n) }">
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="results-counter">
        <span class="counter-number">{{ filteredResults.length }}</span>
        <span class="counter-separator">/</span>
        <span class="counter-total">{{ results.length }}</span>
      </div>
    </div>

    <!-- Header with toggle and controls -->
    <div v-if="finalAnswerReceived" class="carousel-header">
      <div class="carousel-title-area">
        <h3 class="carousel-title">Search Results</h3>
        <span class="results-count" v-if="filteredResults.length > 0">({{ filteredResults.length }}{{ searchQuery ? ` of ${processedResults.length}` : '' }})</span>
      </div>
      <div class="carousel-controls">
        <button
          class="search-toggle-btn"
          @click="showSearch = !showSearch"
          :class="{ 'active': showSearch }"
          title="Toggle search"
          v-if="processedResults.length > 5"
        >
          🔍
        </button>
        <button
          class="edit-mode-btn"
          @click="isEditMode = !isEditMode"
          :class="{ 'active': isEditMode }"
          title="Toggle edit mode"
        >
          ✏️
        </button>
        <button
          class="clear-edits-btn"
          @click="clearAllEdits"
          v-if="hasEdits"
          title="Clear all edits"
        >
          🔄
        </button>
        <button
          class="toggle-btn"
          @click="isExpanded = !isExpanded"
          title="Toggle search results view"
        >
          {{ isExpanded ? '▼' : '▶' }}
        </button>
      </div>
    </div>

    <!-- Search Bar -->
    <div v-if="finalAnswerReceived && isExpanded && showSearch" class="search-bar">
      <div class="search-input-wrapper">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search results by title, URL, or domain..."
          class="search-input"
          @input="onSearchInput"
          ref="searchInput"
        >
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="clear-search-btn"
          title="Clear search"
        >
          ×
        </button>
      </div>
      <div v-if="searchQuery && filteredResults.length === 0" class="no-results">
        No results found for "{{ searchQuery }}"
      </div>
    </div>

    <!-- Collapsed View -->
    <div v-if="finalAnswerReceived && !isExpanded && filteredResults.length > 0" class="collapsed-view" @click="isExpanded = true">
      <div class="collapsed-preview">
        <div class="collapsed-thumbnails">
          <div
            v-for="(result, index) in filteredResults.slice(0, 4)"
            :key="getUniqueKey(result, index)"
            class="collapsed-thumbnail"
            :title="result.title"
          >
            <!-- Properly sized thumbnail images -->
            <img v-if="result.thumbnail || result.image"
                 :src="getThumbnailUrl(result.thumbnail || result.image)"
                 :alt="result.title"
                 @error="handleImageError">
            <!-- Placeholder -->
            <div v-else class="thumbnail-placeholder-mini"></div>
          </div>
          <div v-if="filteredResults.length > 4" class="more-indicator">
            +{{ filteredResults.length - 4 }}
          </div>
        </div>
        <div class="collapsed-info">
          <span class="collapsed-text">{{ filteredResults.length }} search results</span>
          <span v-if="hasEdits" class="edit-indicator">✏️</span>
        </div>
      </div>
    </div>

    <!-- Enhanced Horizontal Carousel -->
    <div class="carousel-container" v-if="!finalAnswerReceived || isExpanded">
      <div class="scroll-arrow left" @click.stop="scrollCarousel('left')" v-if="showScrollButtons && hasScrollLeft">
        <span>◀</span>
      </div>

      <div class="carousel-track" ref="carouselTrack" @scroll="handleScroll">
        <div
          v-for="(result, index) in filteredResults"
          :key="getUniqueKey(result, index)"
          class="carousel-item-wrapper"
          :class="{
            'active': isResultActive(result),
            'placeholder': result.isPlaceholder,
            'edit-mode': isEditMode,
            'edited': isResultEdited(result)
          }"
        >
          <!-- Remove button (only in edit mode) -->
          <button
            v-if="isEditMode && !result.isPlaceholder"
            class="remove-btn"
            @click.stop="removeResult(result)"
            title="Remove this result"
          >
            ×
          </button>

          <!-- Edit button (only in edit mode) -->
          <button
            v-if="isEditMode && !result.isPlaceholder"
            class="edit-btn"
            @click.stop="openEditModal(result)"
            title="Edit this result"
          >
            ✏️
          </button>

          <!-- Edited indicator -->
          <div v-if="isResultEdited(result)" class="edited-indicator" title="This result has been edited">
            ✏️
          </div>

          <a
            :href="result.url"
            target="_blank"
            rel="noopener noreferrer"
            class="carousel-item"
          >
            <div class="result-card">
              <div class="result-image">
                <img v-if="result.thumbnail || result.image"
                    :src="getThumbnailUrl(result.thumbnail || result.image)"
                    :alt="result.title"
                    @error="handleImageError"
                    loading="lazy">
                <div v-else class="thumbnail-placeholder"></div>
              </div>
              <div class="result-info">
                <div class="result-title">{{ truncateTitle(result.title) }}</div>
                <div class="result-source">{{ getDomainFromUrl(result.url) }}</div>
              </div>
            </div>
          </a>
        </div>
      </div>

      <div class="scroll-arrow right" @click.stop="scrollCarousel('right')" v-if="showScrollButtons && hasScrollRight">
        <span>▶</span>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showEditModal" class="edit-modal-overlay" @click="closeEditModal">
      <div class="edit-modal" @click.stop>
        <div class="edit-modal-header">
          <h3>Edit Search Result</h3>
          <button class="close-btn" @click="closeEditModal">×</button>
        </div>
        <div class="edit-modal-body">
          <div class="form-group">
            <label>Title:</label>
            <input v-model="editingResult.title" type="text" class="form-input">
          </div>
          <div class="form-group">
            <label>URL:</label>
            <input v-model="editingResult.url" type="url" class="form-input">
          </div>
          <div class="form-group">
            <label>Image URL:</label>
            <input v-model="editingResult.thumbnail" type="url" class="form-input">
          </div>
        </div>
        <div class="edit-modal-footer">
          <button class="btn-cancel" @click="closeEditModal">Cancel</button>
          <button class="btn-save" @click="saveEdit">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchResultsCarousel',
  props: {
    results: {
      type: Array,
      required: true
    },
    isSearching: {
      type: Boolean,
      default: false
    },
    finalAnswerReceived: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    }
  },
  data() {
    return {
      isExpanded: false,
      isEditMode: false,
      showEditModal: false,
      editingResult: {},
      originalEditingResult: {},
      activeSpeakingUrls: null,
      processedResults: [],

      // Search functionality
      showSearch: false,
      searchQuery: '',

      // Reactive storage for edits and removals
      removedResults: {},  // { messageId: { resultId: true } }
      editedResults: {},   // { messageId: { resultId: editedData } }

      showScrollButtons: true,
      hasScrollLeft: false,
      hasScrollRight: true,
      carouselScrollPosition: 0
    }
  },
  computed: {
    hasEdits() {
      const messageRemovals = this.removedResults[this.messageId] || {};
      const messageEdits = this.editedResults[this.messageId] || {};
      return Object.keys(messageRemovals).length > 0 || Object.keys(messageEdits).length > 0;
    },

    filteredResults() {
      if (!this.searchQuery.trim()) {
        return this.processedResults;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.processedResults.filter(result => {
        const title = (result.title || '').toLowerCase();
        const url = (result.url || '').toLowerCase();
        const domain = this.getDomainFromUrl(result.url).toLowerCase();

        return title.includes(query) ||
               url.includes(query) ||
               domain.includes(query);
      });
    }
  },
  watch: {
    results: {
      immediate: true,
      handler(newResults) {
        if (newResults.length === 0) return;
        this.loadStoredEdits();
        this.applyFiltersAndProcess(newResults);
      }
    },
    finalAnswerReceived: {
      handler(newVal) {
        if (newVal) {
          this.isExpanded = false;
          this.$emit('update:finalAnswer', true);
        }
      }
    },
    messageId: {
      immediate: true,
      handler() {
        this.loadStoredEdits();
        if (this.results.length > 0) {
          this.applyFiltersAndProcess(this.results);
        }
      }
    },
    // Emit filtered results whenever they change
    filteredResults: {
      handler(newFilteredResults) {
        this.$emit('filtered-results-changed', {
          messageId: this.messageId,
          filteredResults: newFilteredResults,
          originalResults: this.results
        });
      },
      immediate: true
    }
  },
  methods: {
    // === LOCAL STORAGE METHODS ===
    getStorageKey(type) {
      return `searchResults_${type}`;
    },

    loadStoredEdits() {
      try {
        // Load removed results
        const storedRemoved = localStorage.getItem(this.getStorageKey('removed'));
        if (storedRemoved) {
          this.removedResults = JSON.parse(storedRemoved);
        }

        // Load edited results
        const storedEdited = localStorage.getItem(this.getStorageKey('edited'));
        if (storedEdited) {
          this.editedResults = JSON.parse(storedEdited);
        }
      } catch (error) {
        console.warn('Failed to load search result edits from localStorage:', error);
        this.removedResults = {};
        this.editedResults = {};
      }
    },

    saveEditsToStorage() {
      try {
        localStorage.setItem(this.getStorageKey('removed'), JSON.stringify(this.removedResults));
        localStorage.setItem(this.getStorageKey('edited'), JSON.stringify(this.editedResults));
      } catch (error) {
        console.warn('Failed to save search result edits to localStorage:', error);
      }
    },

    clearAllEdits() {
      // Clear edits for this message
      this.$set(this.removedResults, this.messageId, {});
      this.$set(this.editedResults, this.messageId, {});
      this.saveEditsToStorage();
      this.applyFiltersAndProcess(this.results); // This will emit updated results

      // Show notification if available
      if (this.$toast && this.$toast.success) {
        this.$toast.success('All edits cleared');
      }
    },

    // === RESULT PROCESSING METHODS ===
    applyFiltersAndProcess(results) {
      if (!results || results.length === 0) {
        this.processedResults = [];
        this.emitFilteredResults(); // Emit even when empty
        return;
      }

      const messageRemovals = this.removedResults[this.messageId] || {};
      const messageEdits = this.editedResults[this.messageId] || {};

      // Filter out removed results and apply edits
      const filteredResults = results
        .filter(result => !messageRemovals[this.getResultId(result)])
        .map(result => {
          const resultId = this.getResultId(result);
          const editedData = messageEdits[resultId];
          return editedData ? { ...result, ...editedData } : result;
        });

      this.animateResults(filteredResults);
      this.emitFilteredResults(); // Emit the processed results
    },

    emitFilteredResults() {
      this.$emit('filtered-results-changed', {
        messageId: this.messageId,
        filteredResults: this.filteredResults,
        originalResults: this.results,
        processedResults: this.processedResults
      });
    },

    getResultId(result) {
      return result.url || result.id || `${result.title}-${result.source}`;
    },

    isResultEdited(result) {
      const messageEdits = this.editedResults[this.messageId] || {};
      return !!messageEdits[this.getResultId(result)];
    },

    // === EDIT/REMOVE METHODS ===
    removeResult(result) {
      const resultId = this.getResultId(result);

      // Ensure the message object exists
      if (!this.removedResults[this.messageId]) {
        this.$set(this.removedResults, this.messageId, {});
      }

      this.$set(this.removedResults[this.messageId], resultId, true);
      this.saveEditsToStorage();
      this.applyFiltersAndProcess(this.results); // This will emit updated results

      this.$emit('remove-result', { result, messageId: this.messageId });

    },

    openEditModal(result) {
      this.originalEditingResult = result;
      this.editingResult = { ...result };
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
      this.editingResult = {};
      this.originalEditingResult = {};
    },

    saveEdit() {
      const resultId = this.getResultId(this.originalEditingResult);

      // Ensure the message object exists
      if (!this.editedResults[this.messageId]) {
        this.$set(this.editedResults, this.messageId, {});
      }

      this.$set(this.editedResults[this.messageId], resultId, this.editingResult);
      this.saveEditsToStorage();
      this.applyFiltersAndProcess(this.results); // This will emit updated results

      if (this.$toast && this.$toast.success) {
        this.$toast.success('Search result updated');
      }

      this.$emit('edit-result', {
        original: this.originalEditingResult,
        edited: this.editingResult,
        messageId: this.messageId
      });

      this.closeEditModal();
    },

    // === IMAGE OPTIMIZATION METHODS ===
    getThumbnailUrl(imageUrl) {
      if (!imageUrl) return '';

      // If it's already a thumbnail or small image, return as-is
      if (imageUrl.includes('thumbnail') || imageUrl.includes('150x150') || imageUrl.includes('200x200')) {
        return imageUrl;
      }

      // Try to convert to thumbnail using common patterns
      // Google Images
      if (imageUrl.includes('googleusercontent.com')) {
        return imageUrl.replace(/=s\d+/, '=s200').replace(/=w\d+/, '=w200');
      }

      // Wikipedia/Wikimedia
      if (imageUrl.includes('upload.wikimedia.org')) {
        // Convert to thumbnail: add /thumb/ and append size
        const parts = imageUrl.split('/');
        const filename = parts[parts.length - 1];
        const basePath = parts.slice(0, -1).join('/');
        return `${basePath}/thumb/${filename}/200px-${filename}`;
      }

      // For other images, try URL parameter approach
      if (imageUrl.includes('?')) {
        return `${imageUrl}&w=200&h=200&fit=crop`;
      } else {
        return `${imageUrl}?w=200&h=200&fit=crop`;
      }
    },

    // === EXISTING METHODS (unchanged) ===
    getUniqueKey(result, index) {
      if (result.isPlaceholder && result.id) {
        return `placeholder-${result.id}`;
      }
      return `${result.url || 'no-url'}-${index}`;
    },

    isResultActive(result) {
      if (!this.activeSpeakingUrls || !result.url) return false;
      if (this.activeSpeakingUrls[result.url]) return true;
      const urls = Object.keys(this.activeSpeakingUrls);
      return urls.some(url =>
        url.includes(result.url) || result.url.includes(url)
      );
    },

    isBarActive(index) {
      const time = Date.now() / 200;
      const amplitude = Math.sin((time + index) / 2) * 0.5 + 0.5;
      return amplitude > 0.5;
    },

    setActiveSpeakingUrls(urls) {
      const urlsObject = {};
      if (urls) {
        if (Array.isArray(urls) && urls.length > 0 && Array.isArray(urls[0])) {
          urls.forEach(entry => {
            if (Array.isArray(entry) && entry.length >= 2) {
              const url = entry[1];
              if (url && typeof url === 'string') {
                urlsObject[url] = true;
              }
            }
          });
        } else if (Array.isArray(urls)) {
          urls.forEach(url => {
            if (url && typeof url === 'string') {
              urlsObject[url] = true;
            }
          });
        } else if (typeof urls === 'string') {
          urlsObject[urls] = true;
        } else if (typeof urls === 'object') {
          Object.assign(urlsObject, urls);
        }
      }

      this.activeSpeakingUrls = urlsObject;

      if (Object.keys(urlsObject).length > 0) {
        this.isExpanded = true;
      }

      setTimeout(() => {
        this.scrollToActiveItem();
      }, 100);
    },

    scrollToActiveItem() {
      if (!this.$refs.carouselTrack || !this.activeSpeakingUrls) {
        return;
      }

      const activeUrls = Object.keys(this.activeSpeakingUrls);
      if (!activeUrls.length) {
        return;
      }

      let activeItem = null;
      const items = this.$refs.carouselTrack.querySelectorAll('.carousel-item');

      for (const item of items) {
        const itemHref = item.getAttribute('href');
        if (activeUrls.some(url => {
          return itemHref === url ||
                (itemHref && url && itemHref.includes(url)) ||
                (url && itemHref && url.includes(itemHref));
        })) {
          activeItem = item;
          break;
        }
      }

      if (activeItem) {
        const trackWidth = this.$refs.carouselTrack.clientWidth;
        const itemLeft = activeItem.offsetLeft;
        const itemWidth = activeItem.offsetWidth;
        const scrollPosition = itemLeft - (trackWidth / 2) + (itemWidth / 2);

        this.$refs.carouselTrack.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });

        this.$nextTick(() => {
          items.forEach(item => item.classList.remove('active-scroll'));
          activeItem.classList.add('active-scroll');
        });
      }
    },

    getDomainFromUrl(url) {
      try {
        const urlObject = new URL(url);
        return urlObject.hostname.replace('www.', '');
      } catch (e) {
        return url;
      }
    },

    truncateTitle(title) {
      return title ? (title.length > 25 ? title.substring(0, 25) + '...' : title) : 'No Title';
    },

    handleImageError(e) {
      e.target.style.display = 'none';
      const placeholder = document.createElement('div');
      placeholder.className = 'thumbnail-placeholder';
      e.target.parentNode.appendChild(placeholder);
    },

    animateResults(results) {
      this.processedResults = [];
      // Don't animate if we're filtering results to avoid duplication
      if (results.length === 0) return;

      // Add results immediately instead of with animation to fix count bug
      this.processedResults = [...results];
    },

    scrollCarousel(direction) {
      if (!this.$refs.carouselTrack) return;

      const scrollAmount = this.$refs.carouselTrack.clientWidth / 2;
      const currentScroll = this.$refs.carouselTrack.scrollLeft;

      if (direction === 'left') {
        this.$refs.carouselTrack.scrollTo({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        this.$refs.carouselTrack.scrollTo({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }

      this.$nextTick(() => {
        this.handleScroll();
      });
    },

    handleScroll() {
      if (!this.$refs.carouselTrack) return;

      const track = this.$refs.carouselTrack;
      this.carouselScrollPosition = track.scrollLeft;
      this.hasScrollLeft = track.scrollLeft > 0;
      this.hasScrollRight = track.scrollLeft < (track.scrollWidth - track.clientWidth - 5);
    },

    // === SEARCH METHODS ===
    onSearchInput() {
      // Auto-expand when searching
      if (this.searchQuery && !this.isExpanded) {
        this.isExpanded = true;
      }
      // Emit filtered results when search changes
      this.$nextTick(() => {
        this.emitFilteredResults();
      });
    },

    clearSearch() {
      this.searchQuery = '';
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
        this.emitFilteredResults();
      });
    }
  },
  mounted() {
    this.loadStoredEdits();
    this.$nextTick(() => {
      if (this.$refs.carouselTrack) {
        this.handleScroll();
      }
      // Emit initial filtered results on mount
      this.emitFilteredResults();
    });
  }
}
</script>

<style scoped>
.search-results-carousel {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  margin: 1rem auto;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
}

.theme--dark .search-results-carousel {
  background-color: #1a202c;
}

/* Header Styles - Matching UserUploadsCarousel */
.carousel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.carousel-title-area {
  display: flex;
  align-items: center;
}

.carousel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .carousel-title {
  color: #e2e8f0;
}

.results-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.carousel-controls {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

/* Edit Mode Styles */
.edit-mode-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-right: 0.25rem;
}

.edit-mode-btn.active {
  background-color: #3182ce;
  color: white;
  border-color: #3182ce;
}

.theme--dark .edit-mode-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.theme--dark .edit-mode-btn:hover {
  background-color: #2d3748;
}

/* Collapsed View Styles - Matching UserUploadsCarousel */
.collapsed-view {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 6px;
  background-color: #f7fafc;
  cursor: pointer;
}

.theme--dark .collapsed-view {
  background-color: #2d3748;
}

.collapsed-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.collapsed-thumbnails {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.collapsed-thumbnail {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  position: relative;
}

.theme--dark .collapsed-thumbnail {
  border-color: #4a5568;
  background-color: #1a202c;
}

.collapsed-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder-mini {
  width: 100%;
  height: 100%;
  background-color: #edf2f7;
}

.theme--dark .thumbnail-placeholder-mini {
  background-color: #2d3748;
}

.more-indicator {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  color: #4a5568;
  font-size: 0.75rem;
  font-weight: 600;
}

.theme--dark .more-indicator {
  background-color: #4a5568;
  color: #e2e8f0;
}

.collapsed-info {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.collapsed-text {
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .collapsed-text {
  color: #a0aec0;
}

/* Neural Animation Row - Simplified and Modernized */
.neural-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 0.75rem;
}

.theme--dark .neural-row {
  background-color: #2d3748;
  border-color: #4a5568;
}

/* Carousel Styles - Matching UserUploadsCarousel */
.carousel-container {
  position: relative;
  display: flex;
  margin: 0 0 1rem 0;
  align-items: center;
  padding: 0;
}

.carousel-track {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 0.5rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  gap: 16px;
}

.carousel-track::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.carousel-item-wrapper {
  position: relative;
  flex: 0 0 auto;
}

.carousel-item-wrapper.edit-mode .carousel-item {
  border: 2px dashed #cbd5e0;
}

.remove-btn, .edit-btn {
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.remove-btn {
  top: -6px;
  right: -6px;
  background-color: #e53e3e;
  color: white;
}

.remove-btn:hover {
  background-color: #c53030;
  transform: scale(1.1);
}

.edit-btn {
  top: -6px;
  right: 16px;
  background-color: #3182ce;
  color: white;
  font-size: 10px;
}

.edit-btn:hover {
  background-color: #2c5282;
  transform: scale(1.1);
}

.carousel-item {
  flex: 0 0 auto;
  width: 160px;  /* Reduced from 180px */
  height: 140px; /* Reduced from 160px */
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
}

/* Ensure text is properly visible and not cut off */
.result-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative; /* Added to ensure proper stacking context */
}

.theme--dark .result-card {
  background: #2d3748;
  border-color: #4a5568;
}

.result-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-color: #cbd5e0;
}

.theme--dark .result-card:hover {
  border-color: #718096;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.result-image {
  height: 50%; /* Reduced from 65% to give more space for text */
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.03);
}

.theme--dark .result-image {
  background: rgba(255, 255, 255, 0.05);
}

.result-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.result-card:hover .result-image img {
  transform: scale(1.05);
}

.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  background-color: #edf2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 0.875rem;
}

.theme--dark .thumbnail-placeholder {
  background-color: #2d3748;
  color: #718096;
}

.result-info {
  padding: 8px 10px;
  height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* Add a special class to ensure text doesn't get cut off */
.truncate-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

/* Better handling of title text */
.result-title {
  font-weight: 500;
  font-size: 0.8rem;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 6px;
  color: #2d3748;
  max-height: 2.6em;
  text-overflow: ellipsis;
  word-break: break-word; /* Allow breaking long words if necessary */
}

.theme--dark .result-title {
  color: #e2e8f0;
}

.result-source {
  font-size: 0.7rem;
  color: #718096;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-bottom: 2px; /* Added to ensure the text isn't too close to the bottom */
}

.theme--dark .result-source {
  color: #a0aec0;
}

/* Scroll Arrows - Matching UserUploadsCarousel */
.scroll-arrow {
  position: absolute;
  width: 40px;
  height: 40px;
  background: #f7fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.theme--dark .scroll-arrow {
  background: #2d3748;
  border-color: #4a5568;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.scroll-arrow:hover {
  background: #edf2f7;
  transform: translateY(-1px);
}

.theme--dark .scroll-arrow:hover {
  background: #4a5568;
}

.scroll-arrow.left {
  left: 10px;
}

.scroll-arrow.right {
  right: 10px;
}

.scroll-arrow span {
  color: #4a5568;
  font-size: 0.9rem;
}

.theme--dark .scroll-arrow span {
  color: #e2e8f0;
}

/* External link indicator - More subtle */
.carousel-item::after {
  content: '';
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s ease;
  opacity: 0.7;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%234a5568' viewBox='0 0 16 16'%3E%3Cpath d='M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z'/%3E%3Cpath d='M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
  background-size: 12px;
  border: 1px solid #e2e8f0;
}

.theme--dark .carousel-item::after {
  background-color: rgba(45, 55, 72, 0.7);
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23e2e8f0' viewBox='0 0 16 16'%3E%3Cpath d='M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z'/%3E%3Cpath d='M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z'/%3E%3C/svg%3E");
  border-color: #4a5568;
}

.carousel-item:hover::after {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.9);
}

.theme--dark .carousel-item:hover::after {
  background-color: rgba(45, 55, 72, 0.9);
}

/* Highlight for active item - Simplified */
.carousel-item.active {
  box-shadow: 0 0 0 2px #3182ce;
}

.theme--dark .carousel-item.active {
  box-shadow: 0 0 0 2px #63b3ed;
}

.carousel-item.active .result-card {
  border-color: #3182ce;
}

.theme--dark .carousel-item.active .result-card {
  border-color: #63b3ed;
}

/* Status area for the neural animation row */
.status-wrapper {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Simplified neural animation */
.neural-animation {
  position: relative;
  width: 24px;
  height: 24px;
}

.neural-pulse {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #3182ce;
  opacity: 0.3;
  animation: neuralPulse 2s infinite;
}

.neural-ring {
  position: absolute;
  width: 12px;
  height: 12px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid #3182ce;
  border-radius: 50%;
  animation: neuralRing 2s infinite;
}

.theme--dark .neural-pulse {
  background: #63b3ed;
}

.theme--dark .neural-ring {
  border-color: #63b3ed;
}

.status-content {
  flex-grow: 1;
}

/* Simplified waveform */
.status-progress {
  height: 24px;
  background: #edf2f7;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

.theme--dark .status-progress {
  background: #2d3748;
}

.waveform {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  height: 100%;
  padding: 0 8px;
}

.waveform-bar {
  flex: 1;
  height: 40%;
  min-width: 3px;
  background: #cbd5e0;
  border-radius: 1px;
  transform-origin: 50% 50%;
  transition: transform 0.2s ease;
  animation: pulse 1.5s ease-in-out infinite;
}

.theme--dark .waveform-bar {
  background: #4a5568;
}

.waveform-bar.active {
  background: #3182ce;
  animation: activePulse 0.8s ease-in-out infinite;
}

.theme--dark .waveform-bar.active {
  background: #63b3ed;
}

/* Results counter - Simplified */
.results-counter {
  font-family: monospace;
  background: #edf2f7;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  color: #4a5568;
}

.theme--dark .results-counter {
  background: #2d3748;
  border-color: #4a5568;
  color: #e2e8f0;
}

.counter-number {
  color: #3182ce;
  font-weight: 600;
}

.theme--dark .counter-number {
  color: #63b3ed;
}

.counter-separator {
  margin: 0 0.25rem;
  opacity: 0.7;
}

.counter-total {
  opacity: 0.7;
}

/* Edit Modal Styles */
.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.theme--dark .edit-modal {
  background: #2d3748;
}

.edit-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.theme--dark .edit-modal-header {
  border-bottom-color: #4a5568;
}

.edit-modal-header h3 {
  margin: 0;
  color: #2d3748;
}

.theme--dark .edit-modal-header h3 {
  color: #e2e8f0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .close-btn:hover {
  background-color: #4a5568;
}

.edit-modal-body {
  padding: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
  color: #2d3748;
}

.theme--dark .form-group label {
  color: #e2e8f0;
}

.form-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875rem;
}

.theme--dark .form-input {
  background: #1a202c;
  border-color: #4a5568;
  color: #e2e8f0;
}

.form-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.edit-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem;
  border-top: 1px solid #e2e8f0;
}

.theme--dark .edit-modal-footer {
  border-top-color: #4a5568;
}

.btn-cancel, .btn-save {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-cancel {
  background-color: #f7fafc;
  color: #2d3748;
  border: 1px solid #e2e8f0;
}

.btn-cancel:hover {
  background-color: #edf2f7;
}

.btn-save {
  background-color: #3182ce;
  color: white;
}

.btn-save:hover {
  background-color: #2c5282;
}

.theme--dark .btn-cancel {
  background-color: #2d3748;
  color: #e2e8f0;
  border-color: #4a5568;
}

.theme--dark .btn-cancel:hover {
  background-color: #4a5568;
}

/* Simplified animations */
@keyframes neuralPulse {
  0%, 100% { transform: scale(0.8); opacity: 0.3; }
  50% { transform: scale(1.2); opacity: 0.1; }
}

@keyframes neuralRing {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: scaleY(0.7); }
  50% { transform: scaleY(0.3); }
}

@keyframes activePulse {
  0%, 100% { transform: scaleY(0.5); }
  50% { transform: scaleY(1); }
}

/* Mobile Responsive Design */
@media screen and (max-width: 768px) {
  .carousel-item {
    width: 140px;  /* Reduced from 160px */
    height: 120px; /* Reduced from 140px */
  }

  .scroll-arrow {
    width: 32px;
    height: 32px;
  }

  .result-title {
    font-size: 0.75rem;
    margin-bottom: 4px;
    -webkit-line-clamp: 2;
    max-height: 2.4em;
  }

  .result-source {
    font-size: 0.65rem;
  }

  .result-info {
    padding: 6px 8px;
  }

  .collapsed-thumbnail {
    width: 28px;
    height: 28px;
  }

  .more-indicator {
    width: 28px;
    height: 28px;
    font-size: 0.7rem;
  }

  .collapsed-text {
    font-size: 0.8rem;
  }

  .neural-row {
    padding: 0.5rem;
  }

  .status-progress {
    height: 20px;
  }

  .results-counter {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }

  .remove-btn, .edit-btn {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }

  .edit-btn {
    font-size: 8px;
  }
}

.edit-indicator {
  color: #3182ce;
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.theme--dark .edit-indicator {
  color: #63b3ed;
}

.clear-edits-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  background: #fed7d7;
  color: #c53030;
  border: 1px solid #feb2b2;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-right: 0.25rem;
}

.clear-edits-btn:hover {
  background: #feb2b2;
}

.theme--dark .clear-edits-btn {
  background: #2d3748;
  color: #fc8181;
  border-color: #4a5568;
}

.theme--dark .clear-edits-btn:hover {
  background: #4a5568;
}

.carousel-item-wrapper.edited {
  position: relative;
}

.edited-indicator {
  position: absolute;
  top: -6px;
  left: -6px;
  width: 16px;
  height: 16px;
  background: #3182ce;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  z-index: 10;
  border: 2px solid white;
}

.theme--dark .edited-indicator {
  background: #63b3ed;
  border-color: #2d3748;
}

/* Improved image sizing */
.result-image img,
.collapsed-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Ensure thumbnails are properly sized */
.collapsed-thumbnail,
.thumbnail-placeholder-mini {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.result-image {
  height: 60px; /* Fixed height for consistency */
  overflow: hidden;
}

/* All other styles remain the same as in the original */
.search-results-carousel {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  margin: 1rem auto;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
}

.theme--dark .search-results-carousel {
  background-color: #1a202c;
}

/* Search functionality styles */
.search-toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-right: 0.25rem;
}

.search-toggle-btn.active {
  background-color: #3182ce;
  color: white;
  border-color: #3182ce;
}

.theme--dark .search-toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.theme--dark .search-toggle-btn:hover {
  background-color: #2d3748;
}

.search-bar {
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background-color: #f7fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.theme--dark .search-bar {
  background-color: #2d3748;
  border-color: #4a5568;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s ease;
}

.theme--dark .search-input {
  background: #1a202c;
  border-color: #4a5568;
  color: #e2e8f0;
}

.search-input:focus {
  outline: none;
  border-color: #3182ce;
  box-shadow: 0 0 0 3px rgba(49, 130, 206, 0.1);
}

.search-input::placeholder {
  color: #a0aec0;
}

.clear-search-btn {
  position: absolute;
  right: 0.5rem;
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #718096;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.clear-search-btn:hover {
  background-color: #edf2f7;
  color: #2d3748;
}

.theme--dark .clear-search-btn:hover {
  background-color: #4a5568;
  color: #e2e8f0;
}

.no-results {
  margin-top: 0.5rem;
  padding: 0.5rem;
  text-align: center;
  color: #718096;
  font-style: italic;
  font-size: 0.875rem;
}

.theme--dark .no-results {
  color: #a0aec0;
}
</style>
